import React, { useState, useEffect, useRef, useMemo } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Container,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Alert,
	Link,
	Divider,
	Skeleton,
} from "@mui/material";
import {
	Add as AddIcon,
	Remove as RemoveIcon,
	DeleteOutline as DeleteIcon,
	WarningAmber,
	RemoveShoppingCartOutlined,
} from "@mui/icons-material";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";

import "../Cart/Cart.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { ASSETS_BASE_URL, DEFAULT_LAYOUT } from "../../config";
import { useAuth } from "../../context/authContext";
import {
	ALERT_MESSAGES,
	BUTTON,
	ROUTES,
	TABLE_HEADERS,
	TITLES,
	UI_TEXT,
} from "../../constants";
import { useTranslation } from "react-i18next";
import formatCurrency from "../../utilities/currency";
import CartTable from "../../components/CartTable/CartTable";
import { getProductsById, getOptions, getCartRecommendations, getCartCTP } from "../../services/cartService";
import CartUpload from "../../components/CartUpload/CartUpload";
import { toast } from "../../context/ToastContext";
import {
	formatOrderQuantity,
	followsMultipleSalesOrderQuantity,
	getMinimumSalesOrderQuantity,
	getMultipleSalesOrderQuantity,
	normalizeOrderQuantity,
	parseOrderQuantity,
} from "../../utilities/orderQuantity";

// Helper function to get product ID, handling both 'ProductID' and 'product_id'
const getNormalizedProductId = (item) => item?.ProductID || item?.product_id;
const getNormalizedMachineId = (item) =>
	item?.MachineID ||
	item?.machine_id ||
	item?.machineId ||
	item?.product_data?.machine_id ||
	null;
const getCartSite = (item, fallbackSite = "") =>
	item?.Site || item?.site_number || fallbackSite;
const isCartActionMachineMatch = (item, machineId) => {
	const itemMachineId = getNormalizedMachineId(item);
	return !machineId || !itemMachineId || Number(itemMachineId) === Number(machineId);
};
const getCartLineKey = (productId, machineId) =>
	`${productId || ""}:${machineId || ""}`;

const Cart = () => {

	const { cartItems, removeFromCart, clearCart, updateCart, isInitialized, refreshCart } = useCart();
	const { selectedSite, selectedSiteCurrency, currencySymbol, selectedSiteData } = useSite();
	const { user, hasPermission, canAddToCart } = useAuth();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [cartProducts, setCartProducts] = useState([]);
	const [groupedCartProducts, setGroupedCartProducts] = useState({});
	const [isLocalInitialized, setIsLocalInitialized] = useState(false);
	const [recommendedProducts, setRecommendedProducts] = useState([]);
	const [showDisclaimerCheckout, setShowDisclaimerCheckout] = useState(false)
	// Optimized quantity management state
	const [quantityInputs, setQuantityInputs] = useState({});
	const [modelViewerSummary, setModelViewerSummary] = useState(null);
	const [pendingMixedSummary, setPendingMixedSummary] = useState(null);

	const prodsRef = useRef(null);
	const ctpDebounceRef = useRef(null); // debounce timer for CTP refresh
	const ctpReqSeq = useRef(0); // track last CTP request to discard stale responses
	const recDebounceRef = useRef(null); // debounce timer for FP (frequently purchased) recommendations
	const recReqSeq = useRef(0); // track last recommendations request
	const { t } = useTranslation();
	const triggerToast = (message, severity = "info") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};
	const [appOptions, setAppOptions] = useState({
		currency: "USD",
		minOrderAmount: 0,
		maxOrderQty: 999,
		creditCardFee: 2.5,
		taxRate: 5,
		shippingCost: 10,
		leadTime: 3,
	});
	const maxQtyAllowed = useMemo(() => {
		const parsed = parseInt(appOptions?.maxOrderQty, 10);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : 999;
	}, [appOptions]);

	const canPlaceOrder = hasPermission("order_management.create_order")
	const canCreateOrderSchedule = hasPermission("order_management.create_order_schedule")
	const canAccessCart = canAddToCart();
	const navigate = useNavigate();
	const location = useLocation();

	const handleNavigateToProduct = (url) => {
		navigate(url);
	};

	useEffect(() => {
		if (!pendingMixedSummary) return;
		setModelViewerSummary(pendingMixedSummary);
		setPendingMixedSummary(null);
		if (pendingMixedSummary?.requestCount && !pendingMixedSummary.cartCount) {
			navigate("/requests");
		}
	}, [pendingMixedSummary, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const mvSource = params.get("mvSource");
		if (mvSource) {
			const cartCount = parseInt(params.get("mvCartCount"), 10) || 0;
			const requestCount = parseInt(params.get("mvRequestCount"), 10) || 0;
			setModelViewerSummary({ cartCount, requestCount, source: mvSource });
			setLoading(true);
			setIsLocalInitialized(false);
			refreshCart();
		} else {
			setModelViewerSummary(null);
		}
	}, [location.search, refreshCart]);

	// Check if product image exists
	const checkImageExists = async (productNumber) => {
		const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${productNumber}.JPG`;
		try {
			const response = await fetch(productImageUrl, { method: "HEAD" });
			return response.ok ? productImageUrl : `${ASSETS_BASE_URL}/stock.png`;
		} catch (error) {
			console.warn("Product image not found, using default image.");
			return `${ASSETS_BASE_URL}/stock.png`;
		}
	};

	const productsInCart = useMemo(() => {
		if (selectedSite) {
			const filteredCartItems =
				cartItems?.filter(
					(item) =>
						item !== null &&
						item !== undefined &&
						getCartSite(item, selectedSite) === selectedSite
				) || [];
			return filteredCartItems;
		} else {
			return []
		}

	}, [cartItems, selectedSite]);

	const uniqueProductIds = useMemo(
		() =>
			[...new Set(productsInCart.map((p) => getNormalizedProductId(p)).filter(Boolean))],
		[productsInCart]
	);

	const cartProductIds = useMemo(
		() => uniqueProductIds.slice().sort().join(","),
		[uniqueProductIds]
	);
	const cartLineSignature = useMemo(
		() =>
			(productsInCart || [])
				.map(
					(item) =>
						`${getNormalizedProductId(item) || ""}:${getNormalizedMachineId(item) || ""}:${getCartSite(item, selectedSite)}`
				)
				.sort()
				.join(","),
		[productsInCart, selectedSite]
	);
	const localCartLineSignature = useMemo(
		() =>
			(cartProducts || [])
				.map(
					(item) =>
						`${getNormalizedProductId(item) || ""}:${getNormalizedMachineId(item) || ""}:${getCartSite(item, selectedSite)}`
				)
				.sort()
				.join(","),
		[cartProducts, selectedSite]
	);


	const fetchCTPForItems = async (cartItems) => {
		try {
			const req = {
				items: cartItems.map((cp) => ({
					product_id: getNormalizedProductId(cp),
					quantity: cp?.Quantity || 1,
				})),
				site_number: selectedSite,
			};
			const resp = await getCartCTP(req);
			const data = (resp?.data || []).filter(d => d !== null && d !== undefined);
			const byId = new Map(data.map((d) => [String(d.product_id), d.ctp]));
			return cartItems.map((cp) => {
				if (!cp) return null; // Defensive null check
				const pid = String(getNormalizedProductId(cp));
				const ctp = byId.get(pid);
				return ctp ? { ...cp, ctp } : cp;
			}).filter(cp => cp !== null);
		} catch (e) {
			console.error('Failed to fetch CTP:', e);
			return cartItems;
		}
	};

	// Debounced CTP updater: optimistic UI first, then refresh CTP once idle
	const scheduleCTPRefresh = (items) => {
		if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
		const seq = ++ctpReqSeq.current;
		ctpDebounceRef.current = setTimeout(async () => {
			try {
				const withCTP = await fetchCTPForItems(items);
				if (seq === ctpReqSeq.current) {
					setCartProducts(withCTP);
				}
			} catch (e) {
				// ignore
			}
		}, 400);
	};

	const cancelPendingCTPRefresh = () => {
		if (ctpDebounceRef.current) {
			clearTimeout(ctpDebounceRef.current);
			ctpDebounceRef.current = null;
		}
		ctpReqSeq.current += 1;
	};


	const fetchProducts = async () => {
		setIsLocalInitialized(false);
		setLoading(true);
		setError(null);
		await fetchOptions();
		if (productsInCart.length === 0) {
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true); // Mark as initialized even if empty
			return;
		}

		try {
			const productIds = uniqueProductIds;
			const reqBody = {
				products: productIds,
				site_number: selectedSite,
			};

			const response = await getProductsById(
				productIds,
				selectedSite,
				productsInCart.map((item) => ({
					product_id: getNormalizedProductId(item),
					machine_id: getNormalizedMachineId(item),
					quantity: item.Quantity || 1,
				}))
			);

			const productData = (response?.data || []).filter(p => p !== null && p !== undefined);
			let adjustedMinimumQuantity = false;
			const updatedCartItems = await Promise.all(
				productsInCart.map(async (item, index) => {
					// Ensure item is not null/undefined before processing
					if (!item) return null; // Return null early for invalid items

					const itemMachineId = getNormalizedMachineId(item);
					const matchedProduct =
						productData.find(
							(product) =>
								parseInt(product.product_id) === parseInt(getNormalizedProductId(item)) &&
								(
									!itemMachineId ||
									Number(product.machine_id) === Number(itemMachineId)
								)
						) ||
						productData.find(
							(product) => parseInt(product.product_id) === parseInt(getNormalizedProductId(item))
						);
					if (matchedProduct) {
						const imageSrc = await checkImageExists(matchedProduct.product_number);
						const showDisclaimer = ["30AA", "30BA", "3AAA", "3ABA"].includes(matchedProduct.product_class);
						if (showDisclaimer && !showDisclaimerCheckout) {
							setShowDisclaimerCheckout(true);
						}
						const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(matchedProduct);
						const multipleSalesOrderQuantity = getMultipleSalesOrderQuantity(matchedProduct);
						const currentQuantity = parseOrderQuantity(item.Quantity) || 1;
						const normalizedQuantity = normalizeOrderQuantity(currentQuantity, matchedProduct);
						if (normalizedQuantity !== currentQuantity) {
							adjustedMinimumQuantity = true;
						}
						return {
							...item,
							Quantity: normalizedQuantity,
							replacement: matchedProduct.replacement || null,
							replaced_by: matchedProduct.replaced_by || matchedProduct.replacedBy || null,
							minimum_sales_order_quantity: minimumSalesOrderQuantity,
							multiple_sales_order_quantity: multipleSalesOrderQuantity,
							product_data: {
								...matchedProduct,
								replaced_by: item.replaced_by || matchedProduct.replaced_by || matchedProduct.replacedBy || null,
								minimum_sales_order_quantity: minimumSalesOrderQuantity,
								multiple_sales_order_quantity: multipleSalesOrderQuantity,
								machine_id:
									getNormalizedMachineId(item) ||
									matchedProduct.machine_id,
							},
							showDisclaimer,
							imageSrc,
						};
					}
					return null; // Return null if no matched product is found
				})
			);
			const finalCartItems = updatedCartItems.filter(item => item !== null); // Filter out any null entries

			if (finalCartItems.length > 0) {
				// Optimistic render immediately
				setCartProducts(finalCartItems);
				if (adjustedMinimumQuantity) {
					const adjustedCartItems = cartItems.map((cartItem) => {
						const matched = finalCartItems.find(
							(item) =>
								getNormalizedProductId(item) === getNormalizedProductId(cartItem) &&
								String(getCartSite(item, selectedSite)) === String(getCartSite(cartItem, selectedSite)) &&
								(
									!getNormalizedMachineId(item) ||
									Number(getNormalizedMachineId(item)) === Number(getNormalizedMachineId(cartItem))
								)
						);
						if (!matched) return cartItem;
						return {
							...cartItem,
							Quantity: matched.Quantity,
							minimum_sales_order_quantity: matched.minimum_sales_order_quantity,
							multiple_sales_order_quantity: matched.multiple_sales_order_quantity,
						};
					});
					updateCart(adjustedCartItems);
					triggerToast(
						t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MINIMUM_CART", {
							defaultValue: "Quantity adjusted to minimum order quantity for one or more parts.",
						}),
						"warning"
					);
				}
				// Debounced CTP refresh
				scheduleCTPRefresh(finalCartItems);
				setLoading(false);
				setIsLocalInitialized(true);
			} else {
				setCartProducts([]);
				setLoading(false);
				setIsLocalInitialized(true);
			}

		} catch (err) {
			setError(t('ERROR_MESSAGES.FAILED_TO_FETCH_DATA', { error: err.message }));
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true);
		}
	}

	const fetchOptions = async () => {
		try {
			const response = await getOptions();

			const responseData = response.data.options;
			setAppOptions(responseData);
		} catch (error) {

		}
	};

	// useEffect(() => {
	// 	if (productsInCart.length === 0) {
	// 		setModelViewerSummary(null);
	// 		setPendingMixedSummary(null);
	// 	}
	// }, [productsInCart.length]);

	useEffect(() => {
		if (!isInitialized) return;
		fetchOptions();
	}, [isInitialized]);

	// Legacy fetchRecommendations kept for reference; now debounced inline in fetchProducts


	useEffect(() => {
		if (!isInitialized) return;

		if (!cartProductIds.length) {
			cancelPendingCTPRefresh();
			setCartProducts([]);
			setGroupedCartProducts({});
			setLoading(false);
			setIsLocalInitialized(true);
			return;
		}

		// Only fetch if cartProducts don’t already cover these IDs
		if (localCartLineSignature === cartLineSignature) {
			return; // no need to fetch again
		}
		fetchProducts();
	}, [cartLineSignature, isInitialized, localCartLineSignature, selectedSite]);

	useEffect(() => {
		if (!isInitialized) return;
		if (!cartProductIds.length || !selectedSite) {
			setRecommendedProducts([]);
			return;
		}

		if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
		const seq = ++recReqSeq.current;
		recDebounceRef.current = setTimeout(async () => {
			try {
				const response = await getCartRecommendations(uniqueProductIds, selectedSite);
				if (seq !== recReqSeq.current) return;
				const recommendations = Array.isArray(response?.data) ? response.data : [];
				const uniqueRecommendations = [
					...new Map(recommendations.map(item => [item.pnum, item])).values()
				];
				setRecommendedProducts(uniqueRecommendations);
			} catch (error) {
				if (seq !== recReqSeq.current) return;
				console.warn("Failed to fetch recommendations:", error);
				setRecommendedProducts([]);
			}
		}, 400);
	}, [cartProductIds, isInitialized, selectedSite, uniqueProductIds]);

	useEffect(() => {
		setQuantityInputs({});
	}, [cartItems, selectedSite]);

	// Cleanup timers on unmount
	useEffect(() => {
		return () => {
			if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
			if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
		};
	}, []);


	useEffect(() => {
		if (cartProducts.length === 0) {
			setGroupedCartProducts({});
			return;
		}

		const getgroupedCartProducts = cartProducts.reduce((groups, item) => {
			if (!item || !item.product_data) return groups; // Extra defensive check
			const { ctp } = item;

			// Determine the correct shipping date
			let shippingDate = null;
			if (ctp?.in_stock && ctp.in_stock.qty > 0) {
				shippingDate = ctp.in_stock.shipping_date;
			} else if (ctp?.out_of_stock && ctp?.out_of_stock?.qty > 0) {
				shippingDate = ctp.out_of_stock.shipping_date;
			} else {
				// While CTP is being fetched, use a sentinel to render skeleton
				shippingDate = '__loading__';
			}

			if (!shippingDate) {
				shippingDate = '__loading__'; // fallback group if nothing found
			}

			// Initialize group if not present
			if (!groups[shippingDate]) {
				groups[shippingDate] = [];
			}

			// Push the item into the right group
			groups[shippingDate].push(item);

			return groups;
		}, {});

		setGroupedCartProducts(getgroupedCartProducts);
	}, [cartProducts]);

	useEffect(() => {

	}, [groupedCartProducts]);

	const parsePrice = (price) => {
		const numPrice = typeof price === "string" ? parseFloat(price) : price;
		return isNaN(numPrice) ? 0 : numPrice;
	};

	const calculateSubtotal = () => {

		return (cartProducts || []).reduce((total, item) => {
			if (!item || !item.product_data) return total; // Skip null/undefined items
			const key = `price_${selectedSiteCurrency}`;
			const currencyPrice = item.product_data?.[key];
			const finalPrice = item.product_data?.purchasable ? currencyPrice : 0;
			const price = parsePrice(finalPrice);

			const cartItem = cartItems.find(
				(ci) =>
					getNormalizedProductId(ci) === getNormalizedProductId(item) &&
					getCartSite(ci, selectedSite) === selectedSite &&
					(
						!getNormalizedMachineId(item) ||
						Number(getNormalizedMachineId(ci)) === Number(getNormalizedMachineId(item))
					)
			);

			const quantity = cartItem ? cartItem?.Quantity : item?.Quantity;
			return total + price * quantity;
		}, 0);
	};

	const calculateTax = () => calculateSubtotal() * 0;
	const calculateTotal = () => calculateSubtotal() + calculateTax();

	const minimumQuantityViolations = useMemo(() => {
		return (cartProducts || [])
			.map((item) => {
				if (!item || !item.product_data) return null;
				const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(item);
				const cartItem = cartItems.find(
					(ci) =>
						getNormalizedProductId(ci) === getNormalizedProductId(item) &&
						getCartSite(ci, selectedSite) === selectedSite &&
						(
							!getNormalizedMachineId(item) ||
							Number(getNormalizedMachineId(ci)) === Number(getNormalizedMachineId(item))
						)
				);
				const quantity = parseOrderQuantity(cartItem?.Quantity ?? item.Quantity) || 1;
				if (
					quantity >= minimumSalesOrderQuantity &&
					followsMultipleSalesOrderQuantity(quantity, item)
				) return null;
				return {
					product_id: item.product_data.product_id,
					product_number: item.product_data.product_number,
					quantity,
					minimumSalesOrderQuantity,
					multipleSalesOrderQuantity: getMultipleSalesOrderQuantity(item),
					isMinimumViolation: quantity < minimumSalesOrderQuantity,
					isMultipleViolation: !followsMultipleSalesOrderQuantity(quantity, item),
				};
			})
			.filter(Boolean);
	}, [cartProducts, cartItems, selectedSite]);

	const handleInputChange = (productId, machineId, inputValue) => {
		const lineKey = getCartLineKey(productId, machineId);
		// Allow completely empty input during editing
		setQuantityInputs((prev) => ({
			...prev,
			[lineKey]: inputValue, // Keep the raw input as-is during typing
		}));
	};

	// Simplified blur handler that only validates on final commit
	const handleInputBlur = (productId, machineId) => {
		const lineKey = getCartLineKey(productId, machineId);
		const inputValue = quantityInputs[lineKey];
		const product = cartProducts.find(
			(p) =>
				getNormalizedProductId(p) === productId &&
				getCartSite(p, selectedSite) === selectedSite &&
				isCartActionMachineMatch(p, machineId)
		);

		if (!product) return;

		const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(product);
		const fallbackQuantity = normalizeOrderQuantity(1, product);
		const maxStock = Math.max(maxQtyAllowed, minimumSalesOrderQuantity, normalizeOrderQuantity(maxQtyAllowed, product));

		const normalizedInput = typeof inputValue === "string" ? inputValue.trim() : inputValue;

		// If completely empty or no valid numeric value, set to the ERP minimum quantity
		if (normalizedInput === "" || normalizedInput === undefined) {
			setQuantityInputs((prev) => ({
				...prev,
				[lineKey]: String(fallbackQuantity),
			}));
			updateQuantity(productId, machineId, fallbackQuantity, "input");

			triggerToast(
				t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MINIMUM", {
					defaultValue: "Quantity adjusted to minimum order quantity: {{quantity}}",
					quantity: formatOrderQuantity(fallbackQuantity),
				}),
				"info"
			);
			return;
		}

		let quantity = parseOrderQuantity(normalizedInput);

		// Handle invalid numbers
		if (!Number.isFinite(quantity)) {
			setQuantityInputs((prev) => ({
				...prev,
				[lineKey]: String(fallbackQuantity),
			}));
			updateQuantity(productId, machineId, fallbackQuantity, "input");
			return;
		}

		// Validate bounds
		let finalQuantity = quantity;
		let showAlert = false;
		let alertMessage = "";

		if (quantity < minimumSalesOrderQuantity) {
			finalQuantity = minimumSalesOrderQuantity;
			showAlert = true;
			alertMessage = t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MINIMUM", {
				defaultValue: "Quantity adjusted to minimum order quantity: {{quantity}}",
				quantity: formatOrderQuantity(minimumSalesOrderQuantity),
			});
		} else if (!followsMultipleSalesOrderQuantity(quantity, product)) {
			finalQuantity = normalizeOrderQuantity(quantity, product);
			showAlert = true;
			alertMessage = t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MULTIPLE", {
				defaultValue: "Quantity adjusted to valid order multiple: {{quantity}}",
				quantity: formatOrderQuantity(finalQuantity),
			});
		} else if (quantity > maxStock) {
			finalQuantity = maxStock;
			showAlert = true;
			alertMessage = t('ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MAX', { maxStock });
		}
		if (finalQuantity > maxStock) {
			finalQuantity = maxStock;
		}

		// Update both input state and cart
		setQuantityInputs((prev) => ({
			...prev,
			[lineKey]: finalQuantity.toString(),
		}));

		updateQuantity(productId, machineId, finalQuantity, "input");

		if (showAlert) {
			triggerToast(alertMessage, "warning");
		}
	};

	// Simplified key press handler
	const handleKeyPress = (productId, machineId, event) => {
		if (event.key === "Enter") {
			event.target.blur();
		} else if (event.key === "Escape") {
			const currentItem = cartItems.find(
				(item) =>
					getNormalizedProductId(item) === productId &&
					getCartSite(item, selectedSite) === selectedSite &&
					isCartActionMachineMatch(item, machineId)
			);
			const currentQuantity = currentItem ? currentItem.Quantity : 1;
			const lineKey = getCartLineKey(productId, machineId);

			setQuantityInputs((prev) => ({
				...prev,
				[lineKey]: currentQuantity.toString(),
			}));
			event.target.blur();
		}
	};

	// Simplified key down handler
	const handleKeyDown = (productId, machineId, event) => {
		// Allow all normal editing keys
		const allowedKeys = [
			"Backspace",
			t("UI_TEXT.TOOLTIP_DELETE"),
			"ArrowLeft",
			"ArrowRight",
			"Home",
			"End",
			"Tab",
			"Enter",
			"Escape",
		];

		const isDigit = /^[0-9]$/.test(event.key);
		const isDecimalKey = event.key === "." || event.key === ",";
		const isCtrlKey = event.ctrlKey || event.metaKey;

		// Only prevent non-numeric, non-control keys
		if (!isDigit && !isDecimalKey && !allowedKeys.includes(event.key) && !isCtrlKey) {
			event.preventDefault();
		}
	};

	const handleRemoveItem = (productId, machineId) => {
		const lineKey = getCartLineKey(productId, machineId);
		cancelPendingCTPRefresh();
		removeFromCart(productId, machineId);
		setCartProducts((prev) =>
			prev.filter(
				(item) =>
					!(
						getNormalizedProductId(item) === productId &&
						getCartSite(item, selectedSite) === selectedSite &&
						isCartActionMachineMatch(item, machineId)
					)
			)
		);
		// Clean up quantity input state
		setQuantityInputs((prev) => {
			const newState = { ...prev };
			delete newState[lineKey];
			return newState;
		});
	};

	const handleQuantityChange = (product_id, machineId, count) => {
		const currentItem = cartItems.find(
			(item) =>
				getNormalizedProductId(item) === product_id &&
				getCartSite(item, selectedSite) === selectedSite &&
				isCartActionMachineMatch(item, machineId)
		);

		if (!currentItem) return;

		const product = cartProducts.find(
			(p) =>
				getNormalizedProductId(p) === product_id &&
				getCartSite(p, selectedSite) === selectedSite &&
				isCartActionMachineMatch(p, machineId)
		);
		const step = getMultipleSalesOrderQuantity(product) || 1;
		const newQuantity = (parseOrderQuantity(currentItem.Quantity) || 0) + (count > 0 ? step : -step);
		const source = count > 0 ? "increment" : "decrement";
		updateQuantity(product_id, machineId, newQuantity, source);
	}

	const updateQuantity = async (productId, machineId, newQuantity, source = "direct") => {
		const cartItem = cartItems.find(
			(item) =>
				getNormalizedProductId(item) === productId &&
				getCartSite(item, selectedSite) === selectedSite &&
				isCartActionMachineMatch(item, machineId)
		);

		const product = cartProducts.find(
			(p) =>
				getNormalizedProductId(p) === productId &&
				getCartSite(p, selectedSite) === selectedSite &&
				isCartActionMachineMatch(p, machineId)
		);

		if (!cartItem || !product) return;

		const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(product);
		const maxStock = Math.max(maxQtyAllowed, minimumSalesOrderQuantity, normalizeOrderQuantity(maxQtyAllowed, product));
		let finalQuantity = newQuantity;

		if (finalQuantity < minimumSalesOrderQuantity) {
			finalQuantity = minimumSalesOrderQuantity;
			if (source === "decrement" || source === "input") {
				triggerToast(
					t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MINIMUM", {
						defaultValue: "Quantity adjusted to minimum order quantity: {{quantity}}",
						quantity: formatOrderQuantity(minimumSalesOrderQuantity),
					}),
					source === "input" ? "warning" : "info"
				);
			}
		} else if (finalQuantity > maxStock) {
			finalQuantity = maxStock;
			if (source === "increment") {
				triggerToast(t('ALERT_MESSAGES.MAX_STOCK_LIMIT_REACHED', { maxStock }), "info");
			} else if (source === "input") {
				triggerToast(t('ALERT_MESSAGES.MAX_UNITS_ALLOWED_ADJUSTED', { maxStock }), "warning");
			}
		}
		finalQuantity = normalizeOrderQuantity(finalQuantity, product);

		// Update cart items
		const updatedCartItems = cartItems.map((item) => {
			if (
				getNormalizedProductId(item) === productId &&
				getCartSite(item, selectedSite) === selectedSite &&
				isCartActionMachineMatch(item, machineId)
			) {
				return { ...item, Quantity: finalQuantity };
			}
			return item;
		});

		const updatedCartProducts = cartProducts.map((item) => {
			if (
				getNormalizedProductId(item) === productId &&
				getCartSite(item, selectedSite) === selectedSite &&
				isCartActionMachineMatch(item, machineId)
			) {
				return { ...item, Quantity: finalQuantity };
			}
			return item;
		});


		// Optimistic update immediately
		setCartProducts(updatedCartProducts);
		// Debounce CTP recalculation for entire cart
		scheduleCTPRefresh(updatedCartProducts);

		updateCart(updatedCartItems);
		setQuantityInputs((prev) => ({
			...prev,
			[getCartLineKey(productId, machineId)]: finalQuantity.toString(),
		}));
	}


	const showLoadingState = loading;

	if (showLoadingState) {
		// Render skeletons matching cart layout
		return (
			<Container maxWidth="xl" className="cart-container">
				<PageTitle title={t("TITLES.CART")} />
				<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "24px", mt: 3 }}>
					<Box sx={{ flex: 1 }}>
						<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px' }}>
							<CardContent>
								<Skeleton variant="text" width={220} height={28} />
								<Divider sx={{ my: 2 }} />
								{[...Array(4)].map((_, idx) => (
									<Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
										<Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 1 }} />
										<Box sx={{ flex: 1 }}>
											<Skeleton variant="text" width="60%" height={22} />
											<Skeleton variant="text" width="40%" height={18} />
										</Box>
										<Skeleton variant="rectangular" width={88} height={36} sx={{ borderRadius: 1 }} />
										<Skeleton variant="text" width={80} height={22} />
										<Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '50%' }} />
									</Box>
								))}
							</CardContent>
						</Card>
					</Box>
					<Box sx={{ flex: 0.4 }}>
						<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px' }}>
							<CardContent>
								<Skeleton variant="text" width={160} height={24} />
								<Divider sx={{ my: 2 }} />
								<Box className="summarySubtotal">
									<Box className="summary-row">
										<Skeleton variant="text" width={120} height={20} />
										<Skeleton variant="text" width={80} height={20} />
									</Box>
								</Box>
								<Box className="summary-row total">
									<Skeleton variant="text" width={100} height={24} />
									<Skeleton variant="text" width={100} height={24} />
								</Box>
								<Box className="summary-row total">
									<Skeleton variant="text" width={'100%'} height={14} />
								</Box>
								<Box className="summary-actions" sx={{ mt: 2 }}>
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Box>
			</Container>
		);
	}

	if (!isLocalInitialized) {
		return (
			<Box className="cart-loader">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}
	return (
		<>
			<Container maxWidth="xl" className="cart-container" sx={{ pb: 4 }} data-tour="cart-part-details">
				<PageTitle title={t("TITLES.CART")} />

				{modelViewerSummary && (
					<Alert severity="info" sx={{ mt: 2 }}>
						<Typography variant="body2">
							{modelViewerSummary.cartCount} part(s) were added to your cart and {modelViewerSummary.requestCount} part(s)
							were sent to the Request List.
						</Typography>
						<Link
							component={RouterLink}
							to="/requests"
							sx={{ mt: 1, display: "inline-flex", fontWeight: 600 }}
						>
							{t('UI_TEXT.VIEW_REQUEST_LIST')}
						</Link>
					</Alert>
				)}


				{cartProducts.length === 0 && !showLoadingState && productsInCart.length === 0 ? (
					<Box sx={{ width: '100%' }}>

						<Alert severity="info" className="empty-cart-alert">
							{t("ALERT_MESSAGES.CART_MESSAGE")}
						</Alert>
						<CartUpload onSummary={setPendingMixedSummary} />
					</Box>
				) : (<>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "space-between",
							gap: "24px",
							mt: 3,
							position: "relative"
						}}
					>
						<Box sx={{ flex: 1 }}>

							<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px' }} >
								<CardContent>
									<CartTable
										groupedCartProducts={groupedCartProducts}
										cartItems={cartItems}
										selectedSite={selectedSite}
										selectedSiteCurrency={selectedSiteCurrency}
										maxQtyAllowed={maxQtyAllowed}
										quantityInputs={quantityInputs}
										handleInputChange={handleInputChange}
										handleInputBlur={handleInputBlur}
										handleKeyPress={handleKeyPress}
										handleKeyDown={handleKeyDown}
										handleQuantityChange={handleQuantityChange}
										handleRemoveItem={handleRemoveItem}
										parsePrice={parsePrice}
										handleNavigateToProduct={handleNavigateToProduct}
										t={t}
									/>
								</CardContent>
							</Card>
							<Button
								variant="outlined"
								color="primary"
								sx={{ mt: 2 }}
								startIcon={<RemoveShoppingCartOutlined />}
								onClick={() => {
									clearCart();
									setCartProducts([]);
									setGroupedCartProducts([]);
									setQuantityInputs({});
									setRecommendedProducts([]);
									setModelViewerSummary(null);
									setPendingMixedSummary(null);
								}}
							>
								{t("BUTTON.CLEAR_CART")}
							</Button>

							<CartUpload onSummary={setPendingMixedSummary} />

							{recommendedProducts.length > 0 && (
								<Box sx={{ mt: 2 }}>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "flexStart",
											justifyContent: "flexStart",
										}}
									>
										<Typography variant="h6">
											{t("UI_TEXT.FREQUENTLY_TOGETHER")}
										</Typography>

										<div
											style={{
												borderBottom: "4px",
												borderColor: "var(--primary-main) !important",
												background: "var(--primary-main) !important",
												backgroundColor: "var(--primary-main) !important",
												borderBottomColor: "var(--primary-main) !important",
												height: "2px",
												margin: "2px 0",
												width: "90px",
											}}
										></div>
									</div>

									<Box>
										{recommendedProducts.slice(0, 5).filter(product => product !== null && product !== undefined).map((product) => (
											<TopProductListItem
												key={product.product_id}
												product={product}
												machine_id={product.machine_id}
											/>
										))}
									</Box>
								</Box>
							)}
						</Box>
						<Box sx={{ flex: 0.4 }}>
							<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px', position: 'sticky', top: DEFAULT_LAYOUT === "layout2" ? "150px" : "100px" }} >
								<CardContent>
									<Typography variant="h6" gutterBottom>
										{t("UI_TEXT.ORDER_SUMMARY")}
									</Typography>
									<Box className="summarySubtotal">
										<Box className="summary-row">
											<Typography>{t("TABLE_HEADERS.SUBTOTAL")}</Typography>
											<Typography>
												{formatCurrency(
													calculateSubtotal(),
													selectedSiteCurrency
												)}
											</Typography>
										</Box>
									</Box>
									<Box className="summary-row total">
										<Typography variant="h6">
											{t("TABLE_HEADERS.TOTAL")}
										</Typography>
										<Typography variant="h6">
											{formatCurrency(calculateTotal(), selectedSiteCurrency)}
										</Typography>
									</Box>
									{/* <Box className="summary-row total">
											<Typography variant="body1" sx={{ fontSize: "11px" }}>
												{t("UI_TEXT.NOTE_SHIPPING_TAXES_INVOICE")}
											</Typography>
										</Box> */}
									{showDisclaimerCheckout && (
										<Box mt={1}>
											<Alert severity="warning">
												<Typography fontWeight="bold">
													{t('UI_TEXT.IMPORTANT_NOTICE_FINAL_SALE')}
												</Typography>
												<Typography sx={{ fontSize: "11px" }}>
													{t('UI_TEXT.CUSTOM_MANUFACTURED_DISCLAIMER')}</Typography>
												<Typography mt={1} sx={{ fontSize: "11px" }}>
													<strong>  {t('UI_TEXT.NO_RETURNS')}</strong> {t('UI_TEXT.FINAL_SALE')}
												</Typography>
											</Alert>
										</Box>
									)}
									<Box className="summary-actions">
										<Button
											variant="contained"
											color="primary"
											fullWidth
											className="checkout-button"
											data-tour="cart-checkout-button"
											onClick={() => {
												navigate(ROUTES.CHECKOUT);
											}}
											disabled={
												!canPlaceOrder ||
												minimumQuantityViolations.length > 0 ||
												calculateTotal().toFixed(2) <
												(parseFloat(appOptions.minOrderAmount) || parseFloat(0))
											}
										>
											{t("BUTTON.CHECKOUT")}
										</Button>
										{calculateTotal().toFixed(2) <
											(parseFloat(appOptions.minOrderAmount) ||
												parseFloat(0)) && (
												<Typography variant="body1" color="error">
													{t("UI_TEXT.MINIMUM_ORDER_AMOUNT_SHOULD_BE")}{" "}
													{currencySymbol}
													{parseFloat(appOptions.minOrderAmount) ||
														parseFloat(0)},{" "}
													{t("UI_TEXT.YOUR_CURRENT_ORDER_TOTAL_IS")}
													{currencySymbol}
													{calculateTotal().toFixed(2)}
												</Typography>
											)}
										{minimumQuantityViolations.map((violation) => (
											<Typography
												key={`${violation.product_id}-order-quantity`}
												variant="body1"
												color="error"
											>
												{violation.isMinimumViolation
													? t("UI_TEXT.MINIMUM_ORDER_QUANTITY_REQUIRED", {
														defaultValue: "{{productNumber}} minimum order quantity is {{quantity}}.",
														productNumber: violation.product_number,
														quantity: formatOrderQuantity(violation.minimumSalesOrderQuantity),
													})
													: t("UI_TEXT.MULTIPLE_ORDER_QUANTITY_REQUIRED", {
														defaultValue: "{{productNumber}} quantity must follow multiple order quantity {{quantity}}.",
														productNumber: violation.product_number,
														quantity: formatOrderQuantity(violation.multipleSalesOrderQuantity),
													})}
											</Typography>
										))}
										{!canPlaceOrder && (
											<Typography variant="body1" color="error">
												{t("UI_TEXT.YOU_ARE_NOT_AUTHORIZED_ORDER")} <br />
												{t(
													"UI_TEXT.PLEASE_CONTACT_THE_CUTOMER_CARE_FOR_FURTHER_DETAILS"
												)}
											</Typography>
										)}
									</Box>
								</CardContent>
							</Card>
						</Box>
					</Box>
				</>)}
			</Container>
		</>
	)
}

export default Cart
